import * as Sentry from "@sentry/react";
import type { User } from "@supabase/supabase-js";
import { posthog } from "./posthog";
import { isChunkLoadError } from "./chunkRecovery";

/**
 * Production error monitoring + alerting.
 *
 * Sentry is the primary alerting path: it captures unhandled exceptions and
 * promise rejections with full stack traces (de-minified via source maps uploaded
 * at build time), groups them into issues, tracks regressions per release, and
 * fans out email / Slack alerts when a new or spiking issue appears.
 *
 * It is wired to PostHog two ways:
 *   1. `postHogIntegration` stamps each Sentry issue with a link to the PostHog
 *      person + session replay, so an error is one click from watching what the
 *      user did before it fired.
 *   2. Sentry also surfaces the PostHog session id as a tag for cross-search.
 *
 * A missing DSN degrades to a no-op (local dev / preview builds), exactly like
 * the PostHog guard — instrumentation is always safe to leave in place.
 */

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const RELEASE = import.meta.env.VITE_APP_VERSION as string | undefined;
const ENVIRONMENT =
  (import.meta.env.VITE_APP_ENV as string | undefined) ||
  (import.meta.env.PROD ? "production" : "development");

export const monitoringEnabled = Boolean(SENTRY_DSN);

export function initMonitoring(): void {
  if (!monitoringEnabled) {
    if (import.meta.env.DEV) {
      console.info("[monitoring] VITE_SENTRY_DSN not set — Sentry disabled (no-op).");
    }
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: ENVIRONMENT,
    release: RELEASE,
    integrations: [
      Sentry.browserTracingIntegration(),
      // Attach a PostHog replay/person link to every Sentry issue.
      posthog.sentryIntegration?.({
        organization: import.meta.env.VITE_POSTHOG_ORG as string | undefined,
        projectId: import.meta.env.VITE_POSTHOG_PROJECT_ID
          ? Number(import.meta.env.VITE_POSTHOG_PROJECT_ID)
          : undefined,
      }),
    ].filter(Boolean) as NonNullable<Parameters<typeof Sentry.init>[0]>["integrations"],
    // Performance sampling — 10% of transactions in prod is plenty of signal
    // without a big data bill; full in dev.
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    // Don't report the noisy, unactionable errors that every SPA sees.
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications.",
      "Non-Error promise rejection captured",
      // NOT ignored: failed dynamic imports. They were filtered here, which hid
      // a real bug — every navigation breaking for users on a stale build after
      // a deploy. They're downgraded to warnings in beforeSend instead, so a
      // deploy-shaped spike is still visible without paging anyone.
      /Load failed/i,
      // Injected by in-app browsers / extensions probing for a native iOS
      // WKWebView bridge — we never touch window.webkit, so it's not our bug.
      /webkit\.messageHandlers/i,
    ],
    // Drop errors thrown from third-party or generated scripts we don't control.
    denyUrls: [
      // vite-plugin-pwa's auto-injected registration. Service-worker registration
      // is rejected by the browser in private mode / when SWs are blocked — an
      // environmental "Error: Rejected" we can neither prevent nor act on.
      /registerSW\.js/i,
    ],
    beforeSend(event, hint) {
      // Cross-link the other direction: tag the Sentry issue with the PostHog
      // session id so you can jump from an issue straight to the replay.
      const sessionId = posthog.get_session_id?.();
      if (sessionId) {
        event.tags = { ...event.tags, posthog_session_id: sessionId };
      }
      // A stale-build chunk failure is expected background noise around a
      // deploy and self-heals via chunkRecovery — but a sustained spike means
      // recovery isn't working. Group them into one warning-level issue rather
      // than one issue per chunk URL.
      if (isChunkLoadError(hint?.originalException) || isChunkLoadError(event.exception?.values?.[0]?.value)) {
        event.level = "warning";
        event.tags = { ...event.tags, chunk_load_failure: "true" };
        event.fingerprint = ["chunk-load-failure"];
      }
      return event;
    },
  });
}

/** Mirror the signed-in user into Sentry so issues are attributed to a person. */
export function setMonitoringUser(user: User | null): void {
  if (!monitoringEnabled) return;
  if (user) {
    Sentry.setUser({ id: user.id, email: user.email ?? undefined });
  } else {
    Sentry.setUser(null);
  }
}

/**
 * Manually report a handled error (e.g. a caught API failure you still want
 * visibility on). Unhandled errors are captured automatically.
 */
export function reportError(error: unknown, context?: Record<string, unknown>): void {
  if (import.meta.env.DEV) console.error("[monitoring]", error, context ?? "");
  if (!monitoringEnabled) return;
  Sentry.captureException(error, context ? { extra: context } : undefined);
}

export { Sentry };
